import { useState, useEffect, useCallback, useContext } from "react";
import "./catalog.scss";
import { useNavigate, useSearchParams } from "react-router-dom";
import { notification } from "antd";
import axios from "axios";
import moment from "moment";

// context
import { AuthContext } from "../../../contexts/authContext";

// icon
import { addIcon } from "../../../constants/icons";

// ui-component
import Loaders from "../../../components/UI/Loaders/Loaders";
import EmptyData from "../../../components/UI/EmptyData/EmptyData";

// interface
interface SectionData {
  _id: string;
  section: string;
  color: string;
  bg_color: string;
  question_color: string;
  sub_sections: string[];
  updatedAt: string;
}

type NotificationType = "success" | "info" | "warning" | "error";

const API_URL = "/api/v1";

const Catalog = () => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const action = searchParams.get("action") || "";
  const status = searchParams.get("status") || "";
  const [api, contextHolder] = notification.useNotification();
  const [sections, setSections] = useState<SectionData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const opNotification = (
    type: NotificationType,
    message: string,
    descripton: string
  ) => {
    api[type]({
      message: message,
      description: descripton,
    });
  };

  const fetchUserRole = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/role/${user.id}`);
      if (res.status === 200 && res.data.success === true) {
        let role = res.data.role;
        return role;
      } else {
        return "user";
      }
    } catch (error: any) {
      console.log("Error fetching user role", error);
    }
  };

  const fetchQuestionnaireSections = useCallback(async () => {
    try {
      const role = await fetchUserRole();
      if (user && role === "admin") {
        const res = await axios.get(`${API_URL}/catalog/list`);
        if (res.status === 200 && res.data.success === true) {
          let sectionlist: any[] = [];
          if (res.data.sections.length > 0) {
            let sectionRes = res.data.sections;
            sectionRes.forEach((sec: any, index: number) => {
              let subsections: string[] = [];
              if (sec.sub_sections.length > 0) {
                sec.sub_sections.forEach((subsec: any) => {
                  subsections.push(subsec.title);
                });
              }
              sectionlist.push({
                _id: sec._id.toString(),
                section: sec.section,
                color: sec.color,
                bg_color: sec.bg_color,
                question_color: sec.question_color,
                sub_sections: subsections,
                updatedAt: moment(sec.updatedAt).format("ll"),
              });
            });
            setSections(sectionlist);
          }
        }
      } else {
        navigate("/dashboard/evaluation");
      }
    } catch (error: any) {
      // console.log("Error fetching questionnaire sections:", error);
      if (
        error.response.status === 403 &&
        error.response.data.access === false
      ) {
        navigate("/dashboard/evaluation");
      }
      if (error.response) {
        const { status } = error.response;
        if (status === 401 || status === 500) {
          setUser(null);
        }
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (action !== "") {
      if (action === "create" && status === "success") {
        opNotification(
          "success",
          "Section Created",
          "New questionnaire section created."
        );
      }
      if (action === "update" && status === "success") {
        opNotification(
          "success",
          "Section Updated",
          "Questionnaire section updated successfully."
        );
      }
      if (action === "delete" && status === "success") {
        opNotification(
          "info",
          "Section Deleted",
          "Questionnaire section deleted successfully."
        );
      }
    }
    // eslint-disable-next-line
  }, [action, status]);

  useEffect(() => {
    fetchQuestionnaireSections();
  }, [fetchQuestionnaireSections]);

  return (
    <div className="catalog">
      {contextHolder}
      <div className="container">
        <div className="appbar">
          <span className="title">Question Catalog</span>
          <button
            onClick={() => {
              navigate("/dashboard/catalog/create");
            }}
          >
            {addIcon} Add Section
          </button>
        </div>
        <div className="listContainer">
          {loading ? (
            <Loaders />
          ) : (
            <>
              <table>
                <thead>
                  <tr>
                    <td className="index">#</td>
                    <td>Section</td>
                    <td>Sub-sections</td>
                    <td className="colors">Colors</td>
                    <td>Last Updated</td>
                  </tr>
                </thead>
                <tbody>
                  {sections.map((section, index) => (
                    <tr
                      key={section._id}
                      onClick={() => {
                        navigate(`/dashboard/catalog/edit/${section._id}`);
                      }}
                    >
                      <td className="index">{index + 1}</td>
                      <td>{section.section}</td>
                      <td>
                        {section.sub_sections.length === 0 &&
                          "No sub-section(s) defined"}
                        <div className="subsecCardHolder">
                          {section.sub_sections.map((ss, ssidx) => (
                            <div
                              className="subsecCard"
                              key={ssidx}
                              style={{
                                backgroundColor: `${section.color}`,
                              }}
                            >
                              {ss}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="colors">
                        <div className="colorPlateHolder">
                          <div
                            className="colorPlate"
                            style={{ backgroundColor: `${section.color}` }}
                          ></div>
                          <div
                            className="colorPlate"
                            style={{ backgroundColor: `${section.bg_color}` }}
                          ></div>
                          <div
                            className="colorPlate"
                            style={{
                              backgroundColor: `${section.question_color}`,
                            }}
                          ></div>
                        </div>
                      </td>
                      <td>{section.updatedAt}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sections.length === 0 && <EmptyData />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Catalog;
